import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTrendingUp,
  FiTarget,
  FiClock,
  FiBookOpen,
  FiActivity,
  FiUser,
  FiUsers,
  FiDownload,
  FiRefreshCw,
  FiZap,
  FiGrid,
  FiList,
  FiEye,
  FiSearch
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import { studentsAPI, analyticsAPI } from '../../services/api';

const StudentsProgress = () => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [timeframe, setTimeframe] = useState('month');
  const [viewMode, setViewMode] = useState('grid');
  const [overallStats, setOverallStats] = useState({});

  const fetchStudentsProgress = useCallback(async () => {
    try {
      setLoading(true);
      const studentsResponse = await studentsAPI.getStudents();
      const studentsData = studentsResponse.data?.users || [];
      const studentsWithProgress = await Promise.all(
        studentsData.map(async (student) => {
          try {
            const progressResponse = await analyticsAPI.getStudentProgress(student._id, timeframe);
            return { ...student, progress: progressResponse.data || {} };
          } catch (error) {
            return {
              ...student,
              progress: {
                overallProgress: 0,
                totalTests: 0,
                averageScore: 0,
                totalStudyTime: 0,
                studyStreak: 0
              }
            };
          }
        })
      );
      setStudents(studentsWithProgress);
      setFilteredStudents(studentsWithProgress);
      calculateOverallStats(studentsWithProgress);
    } catch (error) {
      showError('Failed to load students progress data');
    } finally {
      setLoading(false);
    }
  }, [timeframe, showError]);

  useEffect(() => {
    fetchStudentsProgress();
  }, [fetchStudentsProgress]);

  const calculateOverallStats = (studentsData) => {
    if (!studentsData.length) {
      setOverallStats({});
      return;
    }
    const totalStudents = studentsData.length;
    const totalTests = studentsData.reduce((sum, s) => sum + (s.progress.totalTests || 0), 0);
    const totalStudyTime = studentsData.reduce((sum, s) => sum + (s.progress.totalStudyTime || 0), 0);
    const averageProgress = studentsData.reduce((sum, s) => sum + (s.progress.overallProgress || 0), 0) / totalStudents;
    const averageScore = studentsData.reduce((sum, s) => sum + (s.progress.averageScore || 0), 0) / totalStudents;
    setOverallStats({
      totalStudents,
      totalTests,
      totalStudyTime,
      averageProgress,
      averageScore,
      activeStudents: studentsData.filter((s) => (s.progress.studyStreak || 0) > 0).length
    });
  };

  useEffect(() => {
    let filtered = students;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          (s.firstName || '').toLowerCase().includes(term) ||
          (s.lastName || '').toLowerCase().includes(term) ||
          (s.email || '').toLowerCase().includes(term)
      );
    }
    if (selectedFilter !== 'all') {
      filtered = filtered.filter((s) => {
        const progress = s.progress.overallProgress || 0;
        switch (selectedFilter) {
          case 'excellent': return progress >= 90;
          case 'good': return progress >= 70 && progress < 90;
          case 'average': return progress >= 50 && progress < 70;
          case 'needs-improvement': return progress < 50;
          default: return true;
        }
      });
    }
    setFilteredStudents(filtered);
  }, [students, searchTerm, selectedFilter]);

  const handleTimeframeChange = (newTimeframe) => setTimeframe(newTimeframe);
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStudentsProgress();
    setRefreshing(false);
  };

  const getProgressLevel = (progress) => {
    if (progress >= 90) return { level: 'Excellent', className: 'bg-emerald-100 text-emerald-800' };
    if (progress >= 70) return { level: 'Good', className: 'bg-blue-100 text-blue-800' };
    if (progress >= 50) return { level: 'Average', className: 'bg-amber-100 text-amber-800' };
    return { level: 'Needs Improvement', className: 'bg-red-100 text-red-800' };
  };
  const formatPercentage = (value) => `${Math.round(value || 0)}%`;
  const formatTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading && students.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Students Progress Overview</h1>
          <p className="text-sm text-slate-500 mt-0.5">Monitor and track progress across all your students.</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {['week', 'month', 'quarter', 'year'].map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => handleTimeframeChange(period)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${timeframe === period ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleRefresh} disabled={refreshing} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-60">
              <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <FiDownload className="h-4 w-4" />
              Export Report
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiUsers className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total Students</span>
            <span className="text-sm font-semibold text-slate-900">{overallStats.totalStudents || 0}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiActivity className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-slate-600">Active</span>
            <span className="text-sm font-semibold text-slate-900">{overallStats.activeStudents || 0}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiTrendingUp className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Avg Progress</span>
            <span className="text-sm font-semibold text-slate-900">{formatPercentage(overallStats.averageProgress)}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiTarget className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Avg Score</span>
            <span className="text-sm font-semibold text-slate-900">{formatPercentage(overallStats.averageScore)}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiBookOpen className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total Tests</span>
            <span className="text-sm font-semibold text-slate-900">{overallStats.totalTests || 0}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiClock className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Study Time</span>
            <span className="text-sm font-semibold text-slate-900">{formatTime(overallStats.totalStudyTime)}</span>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search students..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400" />
          </div>
          <select value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
            <option value="all">All Students</option>
            <option value="excellent">Excellent (90%+)</option>
            <option value="good">Good (70–89%)</option>
            <option value="average">Average (50–69%)</option>
            <option value="needs-improvement">Needs Improvement (&lt;50%)</option>
          </select>
          <div className="flex items-center gap-1 sm:ml-auto">
            <button type="button" onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:bg-slate-100'}`} title="Grid"><FiGrid className="h-4 w-4" /></button>
            <button type="button" onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-slate-200 text-slate-800' : 'text-slate-500 hover:bg-slate-100'}`} title="List"><FiList className="h-4 w-4" /></button>
          </div>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm py-14 px-6 text-center">
            <FiUsers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No students match your current search and filter criteria.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStudents.map((student) => {
              const progressLevel = getProgressLevel(student.progress.overallProgress || 0);
              return (
                <div key={student._id} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 hover:border-slate-300 transition-colors">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 shrink-0">
                      <FiUser className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-slate-900 truncate">{student.firstName} {student.lastName}</h3>
                      <p className="text-xs text-slate-500 truncate">{student.email}</p>
                      {student.school && <span className="text-xs text-slate-400">{student.school}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-10 w-10 rounded-full border-2 border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-700" style={{ borderColor: progressLevel.level === 'Excellent' ? '#10b981' : progressLevel.level === 'Good' ? '#3b82f6' : progressLevel.level === 'Average' ? '#f59e0b' : '#ef4444' }}>
                      {formatPercentage(student.progress.overallProgress)}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${progressLevel.className}`}>{progressLevel.level}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-600 mb-4">
                    <span className="flex items-center gap-1"><FiBookOpen className="h-3.5 w-3.5" />{student.progress.totalTests || 0} tests</span>
                    <span className="flex items-center gap-1"><FiTarget className="h-3.5 w-3.5" />{formatPercentage(student.progress.averageScore)} avg</span>
                    <span className="flex items-center gap-1"><FiClock className="h-3.5 w-3.5" />{formatTime(student.progress.totalStudyTime)}</span>
                    <span className="flex items-center gap-1"><FiZap className="h-3.5 w-3.5" />{student.progress.studyStreak || 0} day streak</span>
                  </div>
                  <Link to={`/students/progress/${student._id}`} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
                    <FiEye className="h-4 w-4" />
                    View Details
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Student</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Progress</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Level</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Tests</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Avg Score</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Study Time</th>
                    <th className="px-4 py-3 w-0" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredStudents.map((student) => {
                    const progressLevel = getProgressLevel(student.progress.overallProgress || 0);
                    return (
                      <tr key={student._id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 shrink-0"><FiUser className="h-4 w-4" /></div>
                            <div>
                              <div className="font-medium text-slate-900">{student.firstName} {student.lastName}</div>
                              <div className="text-xs text-slate-500">{student.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700 font-medium">{formatPercentage(student.progress.overallProgress)}</td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${progressLevel.className}`}>{progressLevel.level}</span></td>
                        <td className="px-4 py-3 text-slate-600">{student.progress.totalTests || 0}</td>
                        <td className="px-4 py-3 text-slate-600">{formatPercentage(student.progress.averageScore)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatTime(student.progress.totalStudyTime)}</td>
                        <td className="px-4 py-3">
                          <Link to={`/students/progress/${student._id}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 font-medium">
                            <FiEye className="h-4 w-4" /> View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentsProgress;
